/**
 * BusBookingConfirmationPage.jsx
 *
 * Route:
 *   /book-bus/confirmation
 *
 * Booking lookup priority:
 *
 *   1. ?rc_booking_id= query parameter
 *      - survives browser refresh
 *      - survives direct URL opening
 *
 *   2. location.state.rc_booking_id
 *      - available immediately after navigation
 *
 *   3. location.state.bookingData
 *      - if complete booking data was already passed
 *
 *   4. localStorage rc_bus_booking_id
 *      - fallback for same browser/session
 *
 * IMPORTANT:
 * Booking is fetched using rc_booking_id.
 *
 * Backend should query using rc_booking_id_index, so this continues
 * to work even if the booking user_id was migrated from an anonymous
 * ID to the lead passenger's email.
 */

import React, {
    useEffect,
    useState,
} from "react";

import {
    useLocation,
    useSearchParams,
    useNavigate,
} from "react-router-dom";

import {
    auth,
} from "../firebase";

import BusBookingConfirmation from "../components/bookingBus/BusBookingConfirmation";


const ORCHESTRATOR_URL =
    process.env.NODE_ENV === "development"

        ? "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator/development"

        : "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator";


const ANON_ID_STORAGE_KEY =
    "routechef_bus_anon_id";


const BUS_BOOKING_ID_STORAGE_KEY =
    "rc_bus_booking_id";


// ─────────────────────────────────────────────────────────────────────────────
// Persistent anonymous identity
//
// Used only when user is not logged in.
//
// This should match the same identity strategy used by busBookingAPI.
// ─────────────────────────────────────────────────────────────────────────────

function getOrCreateAnonId() {

    try {

        const existing =
            localStorage.getItem(
                ANON_ID_STORAGE_KEY
            );


        if (existing) {
            return existing;
        }


        const randomPart =
            typeof crypto !== "undefined" &&
            crypto.randomUUID

                ? crypto
                    .randomUUID()
                    .replace(
                        /-/g,
                        ""
                    )
                    .slice(
                        0,
                        16
                    )

                : `${Date.now()}${Math.random()}`
                    .replace(
                        /\D/g,
                        ""
                    )
                    .slice(
                        -16
                    );


        const anonId =
            `anon${randomPart}`;


        localStorage.setItem(
            ANON_ID_STORAGE_KEY,
            anonId
        );


        return anonId;

    } catch (error) {

        console.warn(
            "[BusBookingConfirmationPage] Failed to access anonymous ID:",
            error
        );


        return "anon";
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const BusBookingConfirmationPage = () => {

    const location =
        useLocation();


    const [
        searchParams,
    ] = useSearchParams();


    const navigate =
        useNavigate();


    // Query parameter gets priority because it survives refresh.
    const queryBookingId =
        searchParams.get(
            "rc_booking_id"
        );


    const stateBookingId =
        location.state?.rc_booking_id ||
        location.state?.rcBookingId ||
        null;


    const storedBookingId =
        (() => {

            try {

                return localStorage.getItem(
                    BUS_BOOKING_ID_STORAGE_KEY
                );

            } catch (error) {

                return null;
            }

        })();


    const rcBookingId =
        queryBookingId ||
        stateBookingId ||
        storedBookingId ||
        null;


    const [
        bookingData,
        setBookingData,
    ] = useState(

        location.state?.bookingData ||
        null

    );


    const [
        loading,
        setLoading,
    ] = useState(

        !location.state?.bookingData

    );


    const [
        error,
        setError,
    ] = useState(
        null
    );


    // ─────────────────────────────────────────────────────────────────────────
    // Fetch booking by rc_booking_id
    //
    // This intentionally does NOT depend on the old anonymous user_id.
    //
    // Backend should use rc_booking_id_index.
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {

        // If booking data was explicitly passed and we don't need
        // to refresh it immediately, show it.
        if (
            bookingData &&
            !queryBookingId
        ) {

            setLoading(
                false
            );

            return;
        }


        if (
            !rcBookingId
        ) {

            setError(
                "No booking ID found."
            );


            setLoading(
                false
            );


            return;
        }


        let cancelled =
            false;


        const fetchDetail =
            async () => {

                setLoading(
                    true
                );


                setError(
                    null
                );


                try {

                    const user =
                        auth.currentUser;


                    let userToken =
                        null;


                    let userContact =
                        "";


                    // ─────────────────────────────────────────────────────────
                    // Logged-in user
                    // ─────────────────────────────────────────────────────────

                    if (user) {

                        userToken =
                            await user
                                .getIdToken()
                                .catch(
                                    () => null
                                );


                        userContact =
                            user.email ||

                            (
                                user.phoneNumber
                                    ? user.phoneNumber.replace(
                                        /^\+91/,
                                        ""
                                    )
                                    : ""
                            );

                    }


                    // ─────────────────────────────────────────────────────────
                    // Anonymous user
                    //
                    // Use persistent anon ID.
                    //
                    // Note:
                    // Backend lookup itself should use rc_booking_id_index,
                    // so this From identity should not be required to match
                    // the booking's current DynamoDB user_id.
                    // ─────────────────────────────────────────────────────────

                    if (
                        !userContact
                    ) {

                        userContact =
                            getOrCreateAnonId();

                    }


                    const res =
                        await fetch(
                            ORCHESTRATOR_URL,
                            {

                                method:
                                    "POST",


                                headers: {
                                    "Content-Type":
                                        "application/json",
                                },


                                body:
                                    JSON.stringify({

                                        intent:
                                            "get_bus_booking_detail",


                                        rc_booking_id:
                                            rcBookingId,


                                        From:
                                            `webbot_new:${userContact}`,


                                        headers:

                                            userToken

                                                ? {

                                                    Authorization:
                                                        userToken,

                                                }

                                                : {},

                                    }),

                            }
                        );


                    let result;


                    try {

                        result =
                            await res.json();

                    } catch (jsonError) {

                        throw new Error(
                            "Invalid response while loading booking details."
                        );

                    }


                    if (
                        cancelled
                    ) {
                        return;
                    }


                    if (
                        result?.status === 200 &&
                        result?.data?.booking
                    ) {

                        const booking =
                            result.data.booking;


                        // Parse nested JSON fields so child component gets plain objects
                        const enriched = { ...booking };
                        try {
                            if (typeof enriched.booking_request === 'string') {
                                const br = JSON.parse(enriched.booking_request);
                                enriched.block_payload = br.block_payload || br;
                            }
                        } catch (e) { console.warn('block_payload parse error', e); }
                        try {
                            if (typeof enriched.ticket_details === 'string') {
                                enriched.ticket_details = JSON.parse(enriched.ticket_details);
                            }
                        } catch (e) { console.warn('ticket_details parse error', e); }
                        try {
                            if (typeof enriched.cancellation_policy === 'string') {
                                enriched.cancellation_policy = JSON.parse(enriched.cancellation_policy);
                            }
                        } catch (e) { console.warn('cancellation_policy parse error', e); }

                        setBookingData(enriched);


                        // Keep latest booking ID as fallback.
                        try {

                            localStorage.setItem(
                                BUS_BOOKING_ID_STORAGE_KEY,
                                rcBookingId
                            );

                        } catch (storageError) {

                            console.warn(
                                "[BusBookingConfirmationPage] Could not store booking ID:",
                                storageError
                            );
                        }


                        setError(
                            null
                        );

                    } else {

                        setError(

                            result?.message ||

                            "Booking not found."

                        );

                    }

                } catch (fetchError) {

                    console.error(

                        "[BusBookingConfirmationPage] Failed to load booking:",

                        fetchError

                    );


                    if (
                        !cancelled
                    ) {

                        setError(

                            fetchError?.message ||

                            "Failed to load booking details."

                        );

                    }

                } finally {

                    if (
                        !cancelled
                    ) {

                        setLoading(
                            false
                        );

                    }

                }
            };


        fetchDetail();


        return () => {

            cancelled =
                true;

        };


    }, [
        rcBookingId,
        queryBookingId,
    ]);


    // ─────────────────────────────────────────────────────────────────────────
    // Loading
    // ─────────────────────────────────────────────────────────────────────────

    if (
        loading
    ) {

        return (

            <div className="mt-16 min-h-screen bg-[#F6F8FA] flex items-center justify-center">

                <div className="flex flex-col items-center gap-3 text-gray-400">

                    <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />


                    <span className="text-sm">
                        Loading booking details…
                    </span>

                </div>

            </div>

        );
    }


    // ─────────────────────────────────────────────────────────────────────────
    // Error / booking not found
    // ─────────────────────────────────────────────────────────────────────────

    if (
        error ||
        !bookingData
    ) {

        return (

            <div className="mt-16 min-h-screen bg-[#F6F8FA] flex items-center justify-center">

                <div className="flex flex-col items-center gap-4 text-gray-500">

                    <p className="text-sm text-center max-w-md">

                        {
                            error ||
                            "Booking not found."
                        }

                    </p>


                    {
                        rcBookingId && (

                            <div className="text-xs text-gray-400">

                                Booking reference:{' '}

                                {rcBookingId}

                            </div>

                        )
                    }


                    <div className="flex gap-3">

                        <button

                            onClick={() => {

                                window.location.reload();

                            }}

                            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600"

                        >

                            Try Again

                        </button>


                        <button

                            onClick={() =>
                                navigate(
                                    "/booking-history"
                                )
                            }

                            className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm"

                        >

                            Booking History

                        </button>

                    </div>

                </div>

            </div>

        );
    }


    // ─────────────────────────────────────────────────────────────────────────
    // Ticket Failed
    // ─────────────────────────────────────────────────────────────────────────

    if (bookingData?.status === "TICKET_FAILED") {
        return (
            <div className="mt-16 min-h-screen bg-[#F6F8FA] flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full mx-4 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Booking Failed</h2>
                    <p className="text-gray-500 text-sm mb-2">
                        {bookingData?.remarks || "We were unable to confirm your seats with the operator."}
                    </p>
                    <p className="text-gray-400 text-xs mb-6">
                        Your payment of ₹{bookingData?.final_total_with_discounts || bookingData?.amount || "--"} will be refunded within 5–7 business days.
                    </p>
                    <p className="text-xs text-gray-400 mb-6">
                        Booking ref: <span className="font-mono font-medium">{bookingData?.rc_booking_id}</span>
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate("/")}
                            className="flex-1 px-4 py-2.5 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900"
                        >
                            Back to Home
                        </button>
                        <button
                            onClick={() => navigate("/contact")}
                            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                        >
                            Contact Support
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Confirmation
    // ─────────────────────────────────────────────────────────────────────────

    return (

        <div className="mt-16 min-h-screen bg-[#F6F8FA]">

            <main className="mx-auto max-w-[1200px] px-4 md:px-6 py-4 md:py-6">

                <BusBookingConfirmation

                    bookingData={
                        bookingData
                    }


                    onContactSupport={() =>
                        navigate("/contact")
                    }

                />

            </main>

        </div>

    );
};


export default BusBookingConfirmationPage;
